import { useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";

import {
  TABS_DEFAULTS,
  type TTabsProps,
} from "@/ui/components/navigation/Tabs/Tabs.config";
import {
  getTabFilterKey,
} from "libs/state/src/lib/stores/tanstack/filters/filters.schema";
import {
  useFiltersStore,
} from "libs/state/src/lib/stores/tanstack/filters/useFiltersStore";
import {
  notificationsDispatch,
} from "libs/state/src/lib/stores/tanstack/notifications/notifications.store";

type TUseTabsProps = Pick<
  TTabsProps,
  | "defaultValue"
  | "id"
  | "isTabNavigationDisabled"
  | "onAttemptToNavigateDiabledTabsMessage"
  | "onValueChange"
  | "tabs"
  | "value"
  | "variant"
>;

export const useTabs = ({
  defaultValue,
  id,
  isTabNavigationDisabled,
  onAttemptToNavigateDiabledTabsMessage,
  onValueChange,
  tabs,
  value,
  variant: configuredVariant,
}: TUseTabsProps) => {
  const t = useTranslations("Components.Tabs");
  const variant = configuredVariant ?? TABS_DEFAULTS.variant;
  const fallbackValue = defaultValue ?? tabs[0]?.value;
  const tabValues = useMemo(
    () => new Set(tabs.map((tab) => tab.value)),
    [ tabs ],
  );
  const tabFilterKey = getTabFilterKey(id);
  const { filters, updateFilters } = useFiltersStore();
  const tabFilterValue = filters.tabs[tabFilterKey];
  const selectedValue = tabFilterValue && tabValues.has(tabFilterValue)
    ? tabFilterValue
    : fallbackValue;

  const handleValueChange = useCallback((nextValue: string) => {
    if (isTabNavigationDisabled) {
      notificationsDispatch.pushNotification({
        type: "destructive",
        ...(
          onAttemptToNavigateDiabledTabsMessage ??
          {
            title: t("DisabledNavigation.title"),
            description: t("DisabledNavigation.description"),
          }
        ),
      });
      return;
    }

    updateFilters(tabFilterKey, nextValue);
    onValueChange?.(nextValue);
  }, [
    isTabNavigationDisabled,
    onAttemptToNavigateDiabledTabsMessage,
    onValueChange,
    tabFilterKey,
    t,
    updateFilters,
  ]);

  return {
    handleValueChange,
    selectedValue: value ?? selectedValue,
    variant,
  };
};
